import './TrendLineChart.scss';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Coordinate,
  Line,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Neutral600 } from '@src/utils/colors';
import { CARTESIAN_GRID_STROKE, CARTESIAN_GRID_STROKE_WIDTH } from '../ChartConstants';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import { TrendRow, TrendSeries } from './trend-line-chart-types';

const TICK_FONT_SIZE = 12;
const X_AXIS_HEIGHT = 56;
const Y_AXIS_WIDTH = 48;
const X_TICK_ANGLE = -30;
const X_TICK_MIN_GAP = 16;
const EMPHASIZED_STROKE = 3;
const NORMAL_STROKE = 1.75;
const AREA_OPACITY = 0.12;

const TOOLTIP_ESTIMATED_WIDTH = 240;
const TOOLTIP_VIEWPORT_MARGIN = 12;

interface TrendTooltipPayloadItem {
  name?: string | number;
  value?: number | string | null;
  color?: string;
  payload?: TrendRow;
}

interface TrendTooltipProps {
  active?: boolean;
  payload?: TrendTooltipPayloadItem[];
  coordinate?: Coordinate;
  plotEl: HTMLDivElement | null;
  valueFormatter: (value: number) => string;
}

// Portaled to `document.body` for the same reason as the other charts' tooltips: recharts' own
// tooltip sits inside the card's `overflow: hidden` and gets clipped near the edge.
const TrendTooltip = ({
  active,
  payload,
  coordinate,
  plotEl,
  valueFormatter
}: TrendTooltipProps) => {
  const plotRect = plotEl?.getBoundingClientRect();
  if (!active || !payload || payload.length === 0 || !coordinate || !plotRect) return null;

  const items = payload
    .filter(item => typeof item.value === 'number')
    .sort((a, b) => Number(b.value) - Number(a.value))
    .map(item => ({
      label: String(item.name ?? ''),
      value: valueFormatter(Number(item.value)),
      color: item.color
    }));
  if (items.length === 0) return null;

  const left = Math.min(
    plotRect.left + coordinate.x + TOOLTIP_VIEWPORT_MARGIN,
    window.innerWidth - TOOLTIP_ESTIMATED_WIDTH
  );
  const top = Math.max(
    plotRect.top + coordinate.y - TOOLTIP_VIEWPORT_MARGIN,
    TOOLTIP_VIEWPORT_MARGIN
  );

  return createPortal(
    <div style={{ position: 'fixed', left, top, zIndex: 1000, pointerEvents: 'none' }}>
      <ChartTooltip headerLabel={payload[0].payload?.date ?? ''} items={items} />
    </div>,
    document.body
  );
};

interface TrendLineChartProps {
  rows: TrendRow[];
  // The lines to draw, in order. The first is drawn heavier (and filled, with `fillFirstSeries`).
  series: TrendSeries[];
  height?: number;
  yDomain?: [number, number];
  yTicks?: number[];
  valueFormatter?: (value: number) => string;
  fillFirstSeries?: boolean;
}

const formatPercent = (value: number): string => `${value.toFixed(0)}%`;

// Several series over time (one point per run date). The plot has a fixed height: AutoSizer
// measures it and hands that size to the chart, so a plot whose height depended on its content
// would grow every time the chart re-rendered.
export const TrendLineChart = ({
  rows,
  series,
  height = 340,
  yDomain = [0, 100],
  yTicks,
  valueFormatter = formatPercent,
  fillFirstSeries = false
}: TrendLineChartProps) => {
  const [plotEl, setPlotEl] = useState<HTMLDivElement | null>(null);

  return (
    <div className='trend-line-chart'>
      <div className='trend-line-chart-plot' ref={setPlotEl} style={{ height }}>
        <AutoSizer
          renderProp={({ width = 0, height: sizerHeight = 0 }) => (
            <ComposedChart
              data={rows}
              width={width}
              height={sizerHeight}
              margin={{ top: 8, right: 16, bottom: 0, left: 28 }}
            >
              <CartesianGrid
                stroke={CARTESIAN_GRID_STROKE}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              />
              <XAxis
                dataKey={(row: TrendRow) => row.date}
                axisLine={false}
                tickLine={false}
                interval='preserveStartEnd'
                minTickGap={X_TICK_MIN_GAP}
                angle={X_TICK_ANGLE}
                textAnchor='end'
                height={X_AXIS_HEIGHT}
                tick={{ fontSize: TICK_FONT_SIZE, fill: Neutral600 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={Y_AXIS_WIDTH}
                domain={yDomain}
                ticks={yTicks}
                tickFormatter={(value: number) => valueFormatter(value)}
                tick={{ fontSize: TICK_FONT_SIZE, fill: Neutral600 }}
              />
              <Tooltip
                isAnimationActive={false}
                content={<TrendTooltip plotEl={plotEl} valueFormatter={valueFormatter} />}
              />
              {fillFirstSeries && series[0] && (
                <Area
                  dataKey={(row: TrendRow) => row.values[series[0].key]}
                  name={series[0].label}
                  type='monotone'
                  stroke='none'
                  fill={series[0].color}
                  fillOpacity={AREA_OPACITY}
                  isAnimationActive={false}
                  legendType='none'
                  activeDot={false}
                  tooltipType='none'
                />
              )}
              {series.map((item, index) => (
                <Line
                  key={item.key}
                  dataKey={(row: TrendRow) => row.values[item.key]}
                  name={item.label}
                  type='monotone'
                  stroke={item.color}
                  strokeWidth={index === 0 ? EMPHASIZED_STROKE : NORMAL_STROKE}
                  dot={{ r: index === 0 ? 4 : 3, strokeWidth: 2, fill: 'white' }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          )}
        />
      </div>
    </div>
  );
};
